import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';

export default function AudioPlayerSD({ src, title, className = '' }) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const setAudioData = () => {
            setDuration(audio.duration);
            setCurrentTime(audio.currentTime);
            setIsLoading(false);
        };

        const setAudioTime = () => {
            setCurrentTime(audio.currentTime);
            if (audio.duration) {
                setProgress((audio.currentTime / audio.duration) * 100);
            }
        };

        const handleLoadStart = () => setIsLoading(true);
        const handleCanPlay = () => setIsLoading(false);
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            setProgress(0);
        };

        audio.addEventListener('loadeddata', setAudioData);
        audio.addEventListener('timeupdate', setAudioTime);
        audio.addEventListener('loadstart', handleLoadStart);
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('loadeddata', setAudioData);
            audio.removeEventListener('timeupdate', setAudioTime);
            audio.removeEventListener('loadstart', handleLoadStart);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [src]);

    const togglePlayPause = async () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            try {
                await audio.play();
                setIsPlaying(true);
            } catch (error) {
                console.error('Error playing audio:', error);
            }
        }
    };

    const handleProgressClick = (e) => {
        const audio = audioRef.current;
        if (!audio) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const newTime = percent * duration;

        audio.currentTime = newTime;
        setCurrentTime(newTime);
        setProgress(percent * 100);
    };

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';

        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div className={` max-w-md mx-auto ${className}`}>
            <audio ref={audioRef} src={src} preload="metadata" />

            <div className="space-y-2">
                {/* Progress Bar */}
                <div
                    className="relative h-[.57rem] bg-gray-200 rounded-full cursor-pointer group"
                    onClick={handleProgressClick}
                >
                    <div
                        className="absolute top-0 left-0 h-full bg-[#1A56DB]  transition-all duration-150"
                        style={{ width: `${progress}%` }}
                    />
                    <div
                        className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-[#1A56DB]  opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                        style={{ left: `calc(${progress}% - 8px)` }}
                    />
                </div>

                {/* Time Display */}
                <div className="flex justify-between items-center">
                    <div className="flex justify-center">
                        <button
                            onClick={togglePlayPause}
                            disabled={isLoading}
                            className="flex items-center justify-center   text-textPrimary rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : isPlaying ? (
                                <Pause fill="black" className="w-5 h-5 " />
                            ) : (
                                <Play fill="black" className="w-5 h-5 " />
                            )}
                        </button>
                    </div>
                    <span className="text-sm text-gray-600">
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                </div>

                {/* Play/Pause Button */}
            </div>
        </div>
    );
}
